import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { casesAPI, clientsAPI, documentsAPI, teamAPI, formsAPI, firmAPI } from '../lib/api';
import { useAutoSave, AutoSaveIndicator } from '../hooks/useAutoSave';
import { formatDate, formatCurrency, downloadFile, openFileInNewTab } from '../lib/utils';
import { 
  Briefcase, ArrowLeft, Edit, Calendar, Users, FileText, Clock,
  DollarSign, Scale, AlertTriangle, CheckCircle, User, Building,
  MapPin, Phone, Mail, ExternalLink, Plus, MoreVertical, Trash2,
  Download, Upload, Brain, FormInput, FileCheck, Loader2, Search,
  Play, Eye, Save, Gavel
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const getStatusColor = (status) => {
  const colors = {
    open: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    closed: 'bg-green-100 text-green-800',
    on_hold: 'bg-slate-100 text-slate-800',
  };
  return colors[status] || colors.open;
};

const PRACTICE_AREAS = [
  'Personal Injury', 'Family Law', 'Criminal Defense', 'Real Estate',
  'Corporate Law', 'Immigration', 'Employment Law', 'Estate Planning',
  'Intellectual Property', 'Tax Law', 'Bankruptcy', 'Civil Litigation', 'Other'
];

export const CaseDetails = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Forms state
  const [caseForms, setCaseForms] = useState([]);
  const [formLibrary, setFormLibrary] = useState([]);
  const [formSearchTerm, setFormSearchTerm] = useState('');
  const [showAddFormDialog, setShowAddFormDialog] = useState(false);
  const [addingForm, setAddingForm] = useState(false);
  const [autoFilling, setAutoFilling] = useState(null);
  const [generating, setGenerating] = useState(null);
  
  // Court Summary state
  const [courtSummaryDialog, setCourtSummaryDialog] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [courtSummaryForm, setCourtSummaryForm] = useState({
    jurisdiction: 'Ontario',
    court_name: 'Superior Court of Justice',
    include_billing: false,
    include_communications: true,
    output_type: 'court',
    redact_pii: false,
    redact_minors: true
  });

  // Trust account state
  const [trustAccount, setTrustAccount] = useState(null);
  const [trustTransactions, setTrustTransactions] = useState([]);
  const [showTrustDepositDialog, setShowTrustDepositDialog] = useState(false);
  const [trustDepositForm, setTrustDepositForm] = useState({
    amount: '',
    date_received: new Date().toISOString().split('T')[0],
    method: 'cheque',
    reference_no: '',
    notes: ''
  });
  const [submittingDeposit, setSubmittingDeposit] = useState(false);

  // Auto-save hook for edit form
  const saveCase = useCallback(async (formData) => {
    if (!caseId || !formData.title) return;
    await casesAPI.update(caseId, formData);
    // Refresh case data after auto-save
    const caseRes = await casesAPI.getOne(caseId);
    setCaseData(caseRes.data);
  }, [caseId]);

  const { saveStatus, lastSaved, hasChanges, saveNow, reset } = useAutoSave(
    editForm,
    saveCase,
    {
      delay: 1500,
      enabled: editDialog && editForm.title, // Only auto-save when dialog is open and title exists
      showToast: false, // We'll show inline indicator instead
      onSaveSuccess: () => {
        // Optionally could show inline feedback
      },
      onSaveError: (error) => {
        console.error('Auto-save failed:', error);
      }
    }
  );

  useEffect(() => {
    fetchCaseDetails();
  }, [caseId]);

  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      const [caseRes, docsRes, teamRes, caseFormsRes] = await Promise.all([
        casesAPI.getOne(caseId),
        documentsAPI.getAll(),
        teamAPI.getAll(),
        formsAPI.getCaseForms(caseId).catch(() => ({ data: [] }))
      ]);
      
      setCaseData(caseRes.data);
      setEditForm(caseRes.data);
      setTeamMembers(teamRes.data || []);
      setCaseForms(caseFormsRes.data || []);
      
      // Filter documents for this case
      const caseDocuments = (docsRes.data || []).filter(d => d.case_id === caseId);
      setDocuments(caseDocuments);
      
      // Fetch client info if case has client_id
      if (caseRes.data.client_id) {
        try {
          const clientRes = await clientsAPI.getOne(caseRes.data.client_id);
          setClient(clientRes.data);
        } catch (e) {
          console.error('Failed to fetch client:', e);
        }
      }
      
      // Fetch trust account for this case
      try {
        const trustRes = await api.get(`/trust/case/${caseId}/account`);
        setTrustAccount(trustRes.data?.account);
        setTrustTransactions(trustRes.data?.transactions || []);
      } catch (e) {
        console.error('Failed to fetch trust account:', e);
      }
    } catch (error) {
      console.error('Failed to fetch case:', error);
      toast.error('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  // Form handling functions
  const fetchFormLibrary = async (search = '') => {
    try {
      const response = await formsAPI.getAll({ search, limit: 20 });
      setFormLibrary(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  };

  // Trust deposit handler
  const handleTrustDeposit = async (e) => {
    e.preventDefault();
    setSubmittingDeposit(true);
    try {
      const res = await api.post(`/trust/case/${caseId}/deposit`, {
        amount: parseFloat(trustDepositForm.amount),
        date_received: trustDepositForm.date_received,
        method: trustDepositForm.method,
        reference_no: trustDepositForm.reference_no,
        notes: trustDepositForm.notes
      });
      toast.success(`Trust deposit recorded. New balance: ${formatCurrency(res.data.new_balance)}`);
      setShowTrustDepositDialog(false);
      setTrustDepositForm({
        amount: '',
        date_received: new Date().toISOString().split('T')[0],
        method: 'cheque',
        reference_no: '',
        notes: ''
      });
      // Refresh trust data
      const trustRes = await api.get(`/trust/case/${caseId}/account`);
      setTrustAccount(trustRes.data?.account);
      setTrustTransactions(trustRes.data?.transactions || []);
    } catch (error) {
      console.error('Failed to record trust deposit:', error);
      toast.error(error.response?.data?.detail || 'Failed to record deposit');
    } finally {
      setSubmittingDeposit(false);
    }
  };

  const handleAddForm = async (formId) => {
    setAddingForm(true);
    try {
      await formsAPI.addToCase(caseId, formId);
      toast.success('Form added to case');
      setShowAddFormDialog(false);
      // Refresh case forms
      const response = await formsAPI.getCaseForms(caseId);
      setCaseForms(response.data || []);
    } catch (error) {
      toast.error('Failed to add form');
    } finally {
      setAddingForm(false);
    }
  };

  const handleRemoveForm = async (instanceId) => {
    if (!window.confirm('Remove this form from the case?')) return;
    try {
      await formsAPI.removeInstance(instanceId);
      setCaseForms(prev => prev.filter(f => f.instance_id !== instanceId));
      toast.success('Form removed');
    } catch (error) {
      toast.error('Failed to remove form');
    }
  };

  const handleAutoFill = async (instanceId) => {
    setAutoFilling(instanceId);
    try {
      const response = await formsAPI.autoFill(instanceId);
      toast.success(`Auto-filled ${Object.keys(response.data.filled_data || {}).length} fields`);
      // Refresh case forms
      const formsResponse = await formsAPI.getCaseForms(caseId);
      setCaseForms(formsResponse.data || []);
    } catch (error) {
      toast.error('Failed to auto-fill form');
    } finally {
      setAutoFilling(null);
    }
  };

  const handleGenerateForm = async (instanceId) => {
    setGenerating(instanceId);
    try {
      const response = await formsAPI.generate(instanceId, true, true);
      // Download the file
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `form_${instanceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Form generated and saved to case documents');
      // Refresh
      const [formsRes, docsRes] = await Promise.all([
        formsAPI.getCaseForms(caseId),
        documentsAPI.getAll()
      ]);
      setCaseForms(formsRes.data || []);
      setDocuments((docsRes.data || []).filter(d => d.case_id === caseId));
    } catch (error) {
      toast.error('Failed to generate form. Make sure you auto-fill or manually fill the form first.');
    } finally {
      setGenerating(null);
    }
  };

  const handleUpdateCase = async (e) => {
    e.preventDefault();
    try {
      await casesAPI.update(caseId, editForm);
      toast.success('Case updated successfully');
      setEditDialog(false);
      fetchCaseDetails();
    } catch (error) {
      toast.error('Failed to update case');
    }
  };

  const handleGenerateCourtSummary = async (openInTab = false) => {
    if (!caseData?.client_id) {
      toast.error('This case has no associated client');
      return;
    }
    
    setGeneratingPdf(true);
    try {
      const response = await clientsAPI.generateCourtSummary(caseData.client_id, {
        case_id: caseId,
        ...courtSummaryForm
      });
      
      // Generate filename
      const caseNumber = caseData.case_number?.replace(/[\/\s]/g, '_') || caseId.slice(-8);
      const filename = `Court_Summary_${caseNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      if (openInTab) {
        const success = openFileInNewTab(response.data, 'application/pdf');
        if (success) {
          toast.success('PDF opened in new tab');
          setCourtSummaryDialog(false);
        } else {
          toast.error('Failed to open PDF');
        }
      } else {
        const success = downloadFile(response.data, filename, 'application/pdf');
        if (success) {
          toast.success('Court-Ready Summary downloaded successfully');
          setCourtSummaryDialog(false);
        } else {
          toast.info('Opening in new tab instead...');
          openFileInNewTab(response.data, 'application/pdf');
          setCourtSummaryDialog(false);
        }
      }
    } catch (error) {
      console.error('Failed to generate court summary:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate court summary');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getAssigneeNames = () => {
    if (!caseData?.assigned_to || caseData.assigned_to.length === 0) return 'Unassigned';
    return caseData.assigned_to.map(id => {
      const member = teamMembers.find(m => m.user_id === id);
      return member?.name || 'Unknown';
    }).join(', ');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <PageLoader text="Loading case details..." />;
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900">Case not found</h3>
        <Button onClick={() => navigate('/cases')} variant="link" className="mt-2">
          Back to Cases
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-details-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/cases')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Cases
        </Button>
      </div>

      {/* Case Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center">
                <Briefcase className="h-7 w-7 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{caseData.title}</h1>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge className={getStatusColor(caseData.status)}>
                    {caseData.status?.replace('_', ' ')}
                  </Badge>
                  <Badge variant="outline">{caseData.practice_area}</Badge>
                  {caseData.case_number && (
                    <span className="text-sm text-slate-500">#{caseData.case_number}</span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Created {formatDate(caseData.created_at)}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => setCourtSummaryDialog(true)}
                data-testid="court-summary-btn"
              >
                <Gavel className="h-4 w-4" />
                Court-Ready Summary
              </Button>
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => navigate('/case-health')}
              >
                <Brain className="h-4 w-4" />
                AI Analysis
              </Button>
              <Button 
                className="gap-2"
                onClick={() => setEditDialog(true)}
                data-testid="edit-case-btn"
              >
                <Edit className="h-4 w-4" />
                Edit Case
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {caseData.description && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-wrap">{caseData.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Case Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Case Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseData.court_name && (
                  <div className="flex items-start gap-3">
                    <Scale className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Court</p>
                      <p className="font-medium">{caseData.court_name}</p>
                    </div>
                  </div>
                )}
                {caseData.opposing_party && (
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Opposing Party</p>
                      <p className="font-medium">{caseData.opposing_party}</p>
                    </div>
                  </div>
                )}
                {caseData.opposing_counsel && (
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Opposing Legal Representative</p>
                      <p className="font-medium">{caseData.opposing_counsel}</p>
                    </div>
                  </div>
                )}
                {caseData.canlii_citation && (
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">CanLII Citation</p>
                      <p className="font-medium">{caseData.canlii_citation}</p>
                      {caseData.canlii_url && (
                        <a 
                          href={caseData.canlii_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                        >
                          View on CanLII <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Documents ({documents.length})</CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/documents')}>
                <Plus className="h-4 w-4 mr-1" />
                Add Document
              </Button>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div 
                      key={doc.document_id}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <p className="text-xs text-slate-500">{formatDate(doc.created_at)}</p>
                        </div>
                      </div>
                      {doc.file_url && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No documents attached to this case</p>
              )}
            </CardContent>
          </Card>

          {/* Forms Section */}
          <Card data-testid="case-forms-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FormInput className="h-5 w-5" />
                Forms ({caseForms.length})
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setShowAddFormDialog(true); fetchFormLibrary(); }}
                data-testid="add-form-to-case-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Form
              </Button>
            </CardHeader>
            <CardContent>
              {caseForms.length > 0 ? (
                <div className="space-y-3">
                  {caseForms.map((instance) => (
                    <div 
                      key={instance.instance_id}
                      className="p-3 rounded-lg border border-slate-200 hover:border-slate-300"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <FileCheck className="h-4 w-4 text-slate-400 shrink-0" />
                            <p className="font-medium text-sm truncate">{instance.form?.title || 'Form'}</p>
                          </div>
                          {instance.form?.form_number && (
                            <p className="text-xs text-slate-500 ml-6">Form {instance.form.form_number}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1 ml-6">
                            <Badge variant="outline" className="text-xs capitalize">
                              {instance.status}
                            </Badge>
                            {instance.form?.is_fillable && (
                              <Badge className="text-xs bg-green-50 text-green-700 border-green-200">
                                Fillable
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {instance.form?.is_fillable && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => handleAutoFill(instance.instance_id)}
                                disabled={autoFilling === instance.instance_id}
                                title="Auto-fill from CRM data"
                              >
                                {autoFilling === instance.instance_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => handleGenerateForm(instance.instance_id)}
                                disabled={generating === instance.instance_id || instance.status === 'draft'}
                                title={instance.status === 'draft' ? 'Auto-fill first' : 'Generate filled PDF'}
                              >
                                {generating === instance.instance_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4" />
                                )}
                              </Button>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => window.open(instance.form?.source_url, '_blank')}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View Original
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => handleRemoveForm(instance.instance_id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No forms attached. Click &quot;Add Form&quot; to add forms from your library.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Card */}
          {client && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client</CardTitle>
              </CardHeader>
              <CardContent>
                <Link 
                  to={`/client/${client.client_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      {getInitials(client.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">{client.name}</p>
                    {client.email && (
                      <p className="text-sm text-slate-500">{client.email}</p>
                    )}
                  </div>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Assigned Team */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Assigned Team</CardTitle>
            </CardHeader>
            <CardContent>
              {caseData.assigned_to && caseData.assigned_to.length > 0 ? (
                <div className="space-y-2">
                  {caseData.assigned_to.map((userId) => {
                    const member = teamMembers.find(m => m.user_id === userId);
                    return (
                      <div key={userId} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs bg-blue-100 text-blue-600">
                            {getInitials(member?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{member?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500 capitalize">{member?.role}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-2">No team members assigned</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Documents</span>
                <span className="font-medium">{documents.length}</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Team Members</span>
                <span className="font-medium">{caseData.assigned_to?.length || 0}</span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <Badge className={getStatusColor(caseData.status)}>
                  {caseData.status?.replace('_', ' ')}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Trust Account */}
          <Card data-testid="case-trust-account">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Trust Account
                </CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => setShowTrustDepositDialog(true)}
                  className="gap-1"
                  data-testid="record-deposit-btn"
                >
                  <Plus className="h-4 w-4" />
                  Record Deposit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <p className="text-sm text-emerald-700">Trust Balance</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(trustAccount?.balance || 0)}
                </p>
              </div>
              
              {/* Recent Transactions */}
              {trustTransactions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-500">Recent Transactions</p>
                  {trustTransactions.slice(0, 5).map((tx) => (
                    <div key={tx.transaction_id} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium">{tx.description || tx.type}</p>
                        <p className="text-xs text-slate-500">{formatDate(tx.created_at)}</p>
                      </div>
                      <span className={tx.type === 'deposit' || tx.type === 'adjustment' ? 'text-emerald-600' : 'text-red-600'}>
                        {tx.type === 'deposit' || tx.type === 'adjustment' ? '+' : '-'}
                        {formatCurrency(tx.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {trustTransactions.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-2">No transactions yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Trust Deposit Dialog */}
      <Dialog open={showTrustDepositDialog} onOpenChange={setShowTrustDepositDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Trust Deposit</DialogTitle>
            <DialogDescription>Record a deposit to the trust account for this case</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTrustDeposit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="deposit-amount">Amount *</Label>
              <Input
                id="deposit-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={trustDepositForm.amount}
                onChange={(e) => setTrustDepositForm({ ...trustDepositForm, amount: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deposit-date">Date Received *</Label>
                <Input
                  id="deposit-date"
                  type="date"
                  value={trustDepositForm.date_received}
                  onChange={(e) => setTrustDepositForm({ ...trustDepositForm, date_received: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deposit-method">Payment Method *</Label>
                <Select
                  value={trustDepositForm.method}
                  onValueChange={(v) => setTrustDepositForm({ ...trustDepositForm, method: v })}
                >
                  <SelectTrigger id="deposit-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="eft">EFT / Wire Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="deposit-ref">Reference Number</Label>
              <Input
                id="deposit-ref"
                value={trustDepositForm.reference_no}
                onChange={(e) => setTrustDepositForm({ ...trustDepositForm, reference_no: e.target.value })}
                placeholder="Cheque # or transaction ID"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="deposit-notes">Notes</Label>
              <Textarea
                id="deposit-notes"
                value={trustDepositForm.notes}
                onChange={(e) => setTrustDepositForm({ ...trustDepositForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTrustDepositDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submittingDeposit} className="gap-2">
                {submittingDeposit && <Loader2 className="h-4 w-4 animate-spin" />}
                Record Deposit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={(open) => {
        if (!open && hasChanges) {
          // Save any pending changes before closing
          saveNow();
        }
        setEditDialog(open);
        if (!open) {
          reset(); // Reset auto-save state when closing
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Edit Case</DialogTitle>
                <DialogDescription>Update case information - changes auto-save</DialogDescription>
              </div>
              <AutoSaveIndicator status={saveStatus} lastSaved={lastSaved} className="mr-4" />
            </div>
          </DialogHeader>
          <form onSubmit={handleUpdateCase} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="title">Case Title *</Label>
                <Input
                  id="title"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Practice Area</Label>
                <Select
                  value={editForm.practice_area || ''}
                  onValueChange={(v) => setEditForm({ ...editForm, practice_area: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRACTICE_AREAS.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editForm.status || 'open'}
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="court_name">Court Name</Label>
                <Input
                  id="court_name"
                  value={editForm.court_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, court_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="case_number">Case Number</Label>
                <Input
                  id="case_number"
                  value={editForm.case_number || ''}
                  onChange={(e) => setEditForm({ ...editForm, case_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="opposing_party">Opposing Party</Label>
                <Input
                  id="opposing_party"
                  value={editForm.opposing_party || ''}
                  onChange={(e) => setEditForm({ ...editForm, opposing_party: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="opposing_counsel">Opposing Legal Representative</Label>
                <Input
                  id="opposing_counsel"
                  value={editForm.opposing_counsel || ''}
                  onChange={(e) => setEditForm({ ...editForm, opposing_counsel: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>
                Close
              </Button>
              <Button 
                type="button" 
                onClick={async () => {
                  await saveNow();
                  setEditDialog(false);
                  fetchCaseDetails();
                }}
                disabled={saveStatus === 'saving'}
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save & Close'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Form Dialog */}
      <Dialog open={showAddFormDialog} onOpenChange={setShowAddFormDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Form to Case</DialogTitle>
            <DialogDescription>
              Search and select a form from your library to add to this case.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search forms..."
                value={formSearchTerm}
                onChange={(e) => {
                  setFormSearchTerm(e.target.value);
                  fetchFormLibrary(e.target.value);
                }}
                className="pl-10"
                data-testid="search-forms-dialog-input"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {formLibrary.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {formSearchTerm ? 'No forms found' : 'Your form library is empty. Import forms from the Forms page first.'}
                </p>
              ) : (
                formLibrary.map((form) => (
                  <div 
                    key={form.form_id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => handleAddForm(form.form_id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FormInput className="h-5 w-5 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{form.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {form.form_number && (
                            <span className="text-xs text-slate-500">Form {form.form_number}</span>
                          )}
                          {form.is_fillable && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                              Fillable
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      disabled={addingForm}
                      onClick={(e) => { e.stopPropagation(); handleAddForm(form.form_id); }}
                    >
                      {addingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>
                ))
              )}
            </div>
            
            <div className="text-center pt-2 border-t">
              <Button variant="link" size="sm" onClick={() => { setShowAddFormDialog(false); navigate('/forms'); }}>
                Go to Forms Library →
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Court-Ready Summary Dialog */}
      <Dialog open={courtSummaryDialog} onOpenChange={setCourtSummaryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="h-5 w-5 text-amber-600" />
              Generate Court-Ready Summary
            </DialogTitle>
            <DialogDescription>
              Generate a comprehensive, court-formatted PDF summary for this case
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Case Info */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-900">{caseData?.title}</span>
              </div>
              <p className="text-sm text-blue-700">
                {caseData?.case_number && `#${caseData.case_number} • `}
                {caseData?.practice_area}
              </p>
              {client && (
                <p className="text-sm text-blue-600 mt-1">
                  Client: {client.name}
                </p>
              )}
            </div>

            {/* Jurisdiction */}
            <div className="space-y-2">
              <Label>Jurisdiction</Label>
              <Select 
                value={courtSummaryForm.jurisdiction} 
                onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, jurisdiction: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ontario">Ontario</SelectItem>
                  <SelectItem value="British Columbia">British Columbia</SelectItem>
                  <SelectItem value="Alberta">Alberta</SelectItem>
                  <SelectItem value="Quebec">Quebec</SelectItem>
                  <SelectItem value="Federal">Federal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Court Name */}
            <div className="space-y-2">
              <Label>Court</Label>
              <Select 
                value={courtSummaryForm.court_name} 
                onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, court_name: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Superior Court of Justice">Superior Court of Justice</SelectItem>
                  <SelectItem value="Court of Appeal">Court of Appeal</SelectItem>
                  <SelectItem value="Small Claims Court">Small Claims Court</SelectItem>
                  <SelectItem value="Family Court">Family Court</SelectItem>
                  <SelectItem value="Federal Court">Federal Court</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Output Type */}
            <div className="space-y-2">
              <Label>Output Format</Label>
              <Select 
                value={courtSummaryForm.output_type} 
                onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, output_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="court">Court Filing Format</SelectItem>
                  <SelectItem value="internal">Internal Summary</SelectItem>
                  <SelectItem value="client">Client Report</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Options */}
            <div className="space-y-3 pt-2">
              <Label>Include in Report</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={courtSummaryForm.include_billing}
                    onChange={(e) => setCourtSummaryForm(prev => ({ ...prev, include_billing: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm">Include Billing & Time Records</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={courtSummaryForm.include_communications}
                    onChange={(e) => setCourtSummaryForm(prev => ({ ...prev, include_communications: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm">Include Communications</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={courtSummaryForm.redact_minors}
                    onChange={(e) => setCourtSummaryForm(prev => ({ ...prev, redact_minors: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm">Redact Minor Names</span>
                </label>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> This report is a factual summary and does not constitute legal argument or opinion. 
                All information is sourced from firm records with timestamps and audit trails.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCourtSummaryDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="outline"
                onClick={() => handleGenerateCourtSummary(true)} 
                disabled={generatingPdf}
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
              <Button 
                onClick={() => handleGenerateCourtSummary(false)} 
                disabled={generatingPdf}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
              >
                {generatingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseDetails;
